import { COLORS } from "../core/constants.ts";
import { t } from "../utils/i18n.ts";
import { makeKeyboardActivatable } from "./a11y.ts";
import { UI_SPACE, withUiScale } from "./uiTokens.ts";
import {
  MODAL_FRAME,
  MODAL_SHELL,
  ModalShell,
  modalButton,
  modalFooter,
  modalHeader,
  modalKeycap,
  modalRow,
  modalRule,
  modalSection,
} from "./modalShell.ts";

/** The settings glyph on the header badge — the same ⚙ the HUD gear draws.
 *  Not an i18n string: it is a symbol, identical in every locale. */
const GEAR_GLYPH = "⚙";
/** The footer's key hint. A physical key name, not copy — never translated
 *  (the mockup draws `Esc` in the German panel too). */
const ESC_KEY = "Esc";

const STYLE_ID = "settings-overlay-styles";

export interface SettingsOverlayActions {
  /** ◈ BEDROHUNGSARCHIV — opens the existing `EnemyLibrary` surface unchanged. */
  onOpenLibrary?: () => void;
  /** Master mute (`audio.toggleMaster`) — the SAME intent the gear row emitted. */
  onToggleMuteAll?: () => void;
  /**
   * ÜBERNEHMEN / ZURÜCK / backdrop-click all call this ONE handler. Owner
   * decision OQ-1 = (a): audio applies live, so neither button commits and
   * neither discards — both simply close (spec AC-4).
   */
  onClose?: () => void;
}

/**
 * The in-run SETTINGS modal — the second CONTENT FILL on the shared modal shell
 * (`modalShell.ts`, spec `2026-pause-settings-overlays` AC-2/AC-4/AC-5), and the
 * one that closes AC-2's second half: with this file the shell has two
 * consumers, so "declared once" is a fact rather than an intention.
 *
 * It replaces the 208px corner dropdown the HUD gear used to open
 * (`GearMenu`, `HUD.buildGearMenu`). `GearMenu` itself STAYS — the Boot and
 * Pre-Run gears keep it (spec *Out of scope*); only the in-game use of it goes.
 *
 * Contents, per the owner-approved mockup
 * (`docs/pause_settings_overlays_2026-08.html` §7):
 *   ⚙ badge · EINSTELLUNGEN · "Audio & Spieloptionen"
 *   // AUDIO   ALLES STUMM ▣ · ♫ MUSIK ──●── · ♪ TON ──●──
 *   // SPIEL   ◈ BEDROHUNGSARCHIV
 *   ─────
 *   ✔ ÜBERNEHMEN (primary) · ZURÜCK (quiet) · [Esc]
 *
 * What is deliberately NOT here: RESTART and EXIT (they moved to the pause
 * modal, so the `// STEUERUNG` section disappears outright), and the two untrue
 * keycaps (`R`, `Esc`-on-exit) that died with the dropdown. The footer's `Esc`
 * chip is the one key promise in the design that is TRUE — Escape performs
 * exactly what `ZURÜCK` performs.
 *
 * ── ÜBERNEHMEN commits nothing (owner decision OQ-1 = a, 2026-08-09) ─────────
 * The engine is the authority for audio and the mixer applies live: it emits
 * REQUESTS (`audio.set*Volume` / `audio.toggle*`) and re-renders only from the
 * engine's ECHO (`audio.*VolumeChanged` / `audio.*MuteChanged`) —
 * `AudioMixer.ts:51-84` (re-read 2026-08-10; the previously cited `46-79`
 * started five lines early and cut the `audio.sfxVolumeChanged` echo off its
 * end), closed under `2026-audio-module-audit` AC-6. A slider is therefore
 * audible WHILE dragged, and there is nothing an Apply button could commit.
 * So `ÜBERNEHMEN`, `ZURÜCK` and the backdrop all run the SAME
 * `onClose`. The primary rank on `ÜBERNEHMEN` is visual leadership, not a
 * promise: it does exactly what it says (it ends the settings trip), it just
 * does not do anything EXTRA.
 *
 * ── Panel width: 440 design px — wider than the pause modal's 380, measured ──
 * The mockup itself draws the two panels at different widths
 * (`docs/pause_settings_overlays_2026-08.html:505` `.m-pause{width:calc(380*…)}`
 * vs `:515` `.m-set{width:calc(440*…)}`), so 440 is a transcription, not a
 * divergence. It is also what the German footer needs.
 *
 * Every input below is READ from a shipped artefact, not assumed — which is the
 * correction this arithmetic needed. From `public/fonts/VT323-Regular.ttf`
 * (`head`/`hhea`/`hmtx`/`cmap` walked with a `DataView`, 2026-08-10): VT323
 * advances **0.400 em** for every glyph it HAS (`advanceWidth` 400,
 * `unitsPerEm` 1000) — but its `cmap` has **no `✔` (U+2714)**, so that one glyph
 * falls through the stack to `'Courier New'` at **0.600 em** and costs 4 design
 * px more than VT323 would. Charging it 0.400 em is what put the first pass of
 * these percentages ~1.3 pp low. (`♫ ♪ ◈ ⚙ ⏸` are missing from VT323 too; no
 * width claim below rides on them.) From `index.html:29`, `* { box-sizing:
 * border-box }` — so the panel's 1px `litPanelCss` border comes OUT of its own
 * width. The DE footer row, at the shell's floor-clearing 20 design px and at
 * `--ui-scale × --chrome-scale = 1`, is therefore
 *
 *   ✔ ÜBERNEHMEN  11 VT323 glyphs × 10.8 (0.4×20 + 0.14em 2.8)
 *                  + ✔ 14.8 (0.6×20 + 2.8) + 44 pad + 2 border = 179.6
 *   ZURÜCK         6 glyphs × 10.8         + 44 pad + 2 border = 110.8
 *   [Esc]          3 glyphs × 4.8 (micro 12) + 12 pad + 2 border = 28.4
 *   two 10px gaps                                               =  20.0
 *                                                          total  338.8
 *
 * against a content box of **378** design px at width 440 (440 − 2 border − the
 * shell's shared 2×30 padding) — 39 px of headroom. Because Task-003 scales the
 * padding WITH the panel, that ratio barely moves per tier: the footer occupies
 * **88.8-89.2 %** of the box across the four reference viewports (89.2 % on
 * desktop and iPad, 88.8 % on both phones — the spread is the unscaled 1px
 * borders, nothing else). At the pause modal's 380 the box is **318** and the
 * same footer is **105.5-106.0 %** — it OVERFLOWS everywhere. English fits
 * either way (`✔ APPLY` + `BACK` + chip = 68.8-69.2 %), so this is a
 * German-worst-case width, which is exactly the case i18n parity exists to
 * catch. It is arithmetic over the emitted declarations, not a browser — which
 * is why the fallback advance had to be read rather than assumed.
 *
 * ── Title: 27 design px / 0.22em — the fill's number, not the shell's ────────
 * The shell declares the title ROLE (family, ink, glow, uppercase, wrap guard)
 * and the pause fill takes its 32 px. This fill overrides the SIZE to the
 * mockup's own settings-specific `:518` `.m-set .m-title{font-size:calc(27*…);
 * letter-spacing:.22em}` — the same kind of declared divergence as the 440
 * width, and for the same reason: "declared once" governs the shell's MATERIAL,
 * not every number in it. Measured on the header text column (the content box
 * less the 46 badge and the 12 head gap), `EINSTELLUNGEN` fills **90.8-90.9 %**
 * at the shell's 32 and **67.8-67.9 %** at 27 — against the pause title's
 * 56.2-56.3 %. As drawn the two titles are siblings; at 32 they are 34 pp apart,
 * i.e. the one element unified by hand is the one that stops reading as one
 * system. EN (`SETTINGS`, 8 glyphs) fills 41.7-41.8 %. One line on every tier in
 * both locales.
 *
 * Height is the tightest thing this spec ships: 26 padding + 62 head + 2×41
 * section heads + 4×44 rows + 8 mixer gap + 39 rule + 47 footer + 28 padding ≈
 * **468 design px**, i.e. ~90 % of the stage height on an iPhone SE (88 % on a
 * 14 Pro, 65 % on desktop/iPad) — the 27px title bought 5.5 px of that back.
 * It fits, and no row beyond the mockup's is added for exactly that reason.
 * `max-height`/`overflow-y` below is a fallback for the case this arithmetic is
 * wrong — it is inert at all four tiers. Containment in a REAL browser is spec
 * AC-12 (Task-005), which owns the number.
 */
export class SettingsOverlay {
  private shell: ModalShell;
  private readonly actions: SettingsOverlayActions;
  /** The mockup's filled square — reflects the ECHOED master-mute state. */
  private readonly muteBox: HTMLSpanElement;
  private readonly muteRow: HTMLElement;

  /**
   * @param mixerEl the HUD's ALREADY-CONSTRUCTED `AudioMixer.el`. Passed in
   *   rather than created here: spec AC-10 requires ONE `createAudioMixer` per
   *   HUD, reused across open → close → open, so volumes and mutes cannot drift.
   *   This overlay never reads or writes an audio value.
   */
  constructor(
    parent: HTMLElement,
    mixerEl: HTMLElement | null,
    actions: SettingsOverlayActions = {},
  ) {
    this.actions = actions;
    SettingsOverlay.injectStyles();

    this.shell = new ModalShell(parent, {
      id: "settings-overlay",
      panelId: "settings-card",
      panelClass: "settings-modal",
      labelledBy: "settings-title",
      // The dimmed area closes settings — the same gesture the dropdown's
      // click-away had, now on the shared BL-198 dismiss contract.
      onBackdropClick: () => this.actions.onClose?.(),
      // AC-8 — the footer's `Esc` chip made honest: Escape runs the SAME single
      // `onClose` that ÜBERNEHMEN, ZURÜCK and the backdrop run, which is why
      // "back" means the same thing on both entry paths (AC-16 — the owner is
      // `HUD.closeSettings`, the only place that knows where settings was
      // opened from).
      onEscape: () => this.actions.onClose?.(),
    });

    this.shell.append(
      modalHeader({
        icon: GEAR_GLYPH,
        title: t("settings.title"),
        subtitle: t("settings.subtitle"),
        titleId: "settings-title",
        subtitleId: "settings-subtitle",
      }),
      modalSection(t("hud.audio.section")),
    );

    // ── ALLES STUMM ──────────────────────────────────────────────────────────
    // Unchanged meaning (spec Assumption): still the existing master-mute
    // intent, only its presentation moves from the `◻`/`◼` glyph to the
    // mockup's square. `aria-pressed` carries the state for AT, so the box is
    // never the only channel.
    this.muteRow = modalRow("settings-mute");
    const muteLabel = document.createElement("span");
    muteLabel.className = "settings-lbl";
    muteLabel.textContent = t("hud.muteAll");
    this.muteBox = document.createElement("span");
    this.muteBox.className = "settings-chk";
    this.muteBox.setAttribute("aria-hidden", "true");
    this.muteRow.appendChild(muteLabel);
    this.muteRow.appendChild(this.muteBox);
    this.muteRow.setAttribute("aria-label", t("hud.muteAll"));
    makeKeyboardActivatable(this.muteRow, () =>
      this.actions.onToggleMuteAll?.(),
    );
    this.shell.append(this.muteRow);
    this.syncMuteAll(false);

    // ── ♫ MUSIK / ♪ TON ──────────────────────────────────────────────────────
    // The reused mixer's own two rows are ADOPTED into the shell's row shape
    // (44px floor, wrap guard, hover) instead of the settings sheet restating
    // it — the shape stays declared exactly once (AC-2).
    if (mixerEl) {
      for (const row of Array.from(
        mixerEl.querySelectorAll<HTMLElement>(".audio-mixer-row"),
      )) {
        row.classList.add(MODAL_SHELL.row);
      }
      this.shell.append(mixerEl);
    }

    // ── // SPIEL ─────────────────────────────────────────────────────────────
    this.shell.append(modalSection(t("settings.game.section")));
    const libraryRow = modalRow("settings-library");
    const libraryLabel = document.createElement("span");
    libraryLabel.className = "settings-lbl";
    libraryLabel.textContent = t("hud.library");
    libraryRow.appendChild(libraryLabel);
    libraryRow.setAttribute("aria-label", t("hud.library"));
    makeKeyboardActivatable(libraryRow, () => this.actions.onOpenLibrary?.());
    this.shell.append(libraryRow, modalRule());

    // ── footer ───────────────────────────────────────────────────────────────
    const foot = modalFooter();
    foot.appendChild(
      modalButton({
        id: "settings-apply-btn",
        label: t("settings.apply"),
        rank: "primary",
        onActivate: () => this.actions.onClose?.(),
      }),
    );
    foot.appendChild(
      modalButton({
        id: "settings-back-btn",
        label: t("settings.back"),
        rank: "quiet",
        onActivate: () => this.actions.onClose?.(),
      }),
    );
    foot.appendChild(modalKeycap(ESC_KEY));
    this.shell.append(foot);
  }

  /** Reflect the engine's ECHOED master-mute state on the square. */
  syncMuteAll(muted: boolean): void {
    this.muteBox.classList.toggle("settings-chk-on", muted);
    this.muteRow.setAttribute("aria-pressed", String(muted));
  }

  show(): void {
    this.shell.show();
  }

  hide(): void {
    this.shell.hide();
  }

  get visible(): boolean {
    return this.shell.visible;
  }

  destroy(): void {
    this.shell.destroy();
  }

  /**
   * The settings modal's CONTENT-ONLY stylesheet: panel size, the label flex and
   * the mute square. Deliberately carries no backdrop, no panel material, no
   * button ranks, no row shape and no spacing ladder — all of those are the
   * shell's (AC-2), including the reduced-motion seam (AC-14). Colour resolves
   * from `COLORS` through `color-mix()`; no inline hex (D-225).
   *
   * Three notes that belong here rather than inside the emitted string (a CSS
   * comment is string CONTENT and ships in the size-gated bundle):
   *   · the 440 width and the 27/0.22em title are the mockup's own drawn
   *     settings-specific values — the derivation, the German-footer
   *     measurement and the column fills are in the class doc-block above. The
   *     title is the ONE type size this fill declares; every other role still
   *     resolves from the shell, and the shell's own 32 (the pause title) is
   *     untouched;
   *   · `.settings-lbl { flex: 1 }` lets the label take the slack so a trailing
   *     control (the square) pins right;
   *   · `.settings-chk` FILLED means muted, as the owner's image draws it; the
   *     row's `aria-pressed` carries the same state, so the box is never the
   *     only channel.
   *   · `max-height`/`overflow-y` is a containment FALLBACK. It stays INERT on
   *     every phone tier this project TUNES for, and that is the bar it is held
   *     to — **not** "it can never engage", which an earlier revision of this
   *     note asserted and which is false. Measured, real Chromium, 2026-08-11:
   *     at **568×320** (iPhone SE 1st gen / 5s — the smallest class
   *     `IPHONEOS_DEPLOYMENT_TARGET = 15.0` still admits) the stage scale falls
   *     to **0.3554**, `computeChromeScale` pins its 1.5 ceiling, and the
   *     fallback **DOES** engage: `scrollHeight 895 > clientHeight 844`. The
   *     panel is nevertheless still fully **CONTAINED** there (top 9.6, bottom
   *     310.3 in a 320-px viewport), so spec **AC-12 holds** — what degrades is
   *     reachability-by-scroll inside the panel, not clipping.
   *
   *     **OWNER SCOPING DECISION, 2026-08-11 — this device class is out of
   *     scope**, verbatim: *"Unser Fokus fuer das spiel ist iOS und iPadOS"*
   *     and *"Aber iOS die von apple nicht mehr unterstützt wird weil zu alt…
   *     ist uns auch auch egal"*. The only devices that reach 568×320 are the
   *     iPhone SE 1st gen / 5s class, and the devices capped at iOS 15 are
   *     SE1 / 6s / 7 — all past Apple's support window. So the fallback
   *     engaging there is not a defect to carry. The `.xcodeproj`'s
   *     `IPHONEOS_DEPLOYMENT_TARGET = 15.0` is left UNCHANGED here on purpose:
   *     raising it to 16.0 would make the scoping formal and drop the class
   *     outright, but that edits the Xcode project and narrows the store's
   *     supported-device list, so it is the owner's to authorise explicitly
   *     rather than a side effect of a CSS spec. F-42, closed 2026-08-11.
   *
   *     **On the FOCUS platforms the fallback is inert and the 44 px rail is
   *     fully met** — measured the same day, same browser: iPad mini 8.3
   *     1024×744 → scale 0.8265, `phoneTier` false, min touch **45.46**;
   *     iPad 10.9 1180×820 → 0.9111, **50.11**; iPad Pro 12.9 1366×1024 →
   *     1.1374, **62.56**. All contained, none scrolling. AC-11's shortfall is
   *     a phone-tier phenomenon only; on iPadOS the original 44 px reading of
   *     it would have passed unamended. Task-005 raised it 92 % →
   *     **94 %** because the AC-11 spacing fixes (the mixer's `margin-top` and
   *     its `lg` row gap) grew the panel. Measured, not modelled — real
   *     Chromium on the booted page, iPhone SE landscape 667×375, DE,
   *     `pointer:coarse`, 2026-08-11: the content needs **829 design px**, and
   *     `max-height` caps the BORDER box (`box-sizing: border-box`), so 92 % of
   *     the **900**-design-px overlay root capped it at 828 → **826** px of
   *     content, and the fallback engaged (`scrollHeight 829 > clientHeight
   *     826`, driven as a counterfactual). 94 % caps at **846** against the
   *     panel's real **830.8** → inert again, **15.2 design px** of headroom,
   *     and the panel still cannot reach the stage edge. On the 14 Pro the
   *     content is 806 and 92 % already allowed 826, so that tier never bit.
   *     Containment itself is spec AC-12. (The root is the overlay backdrop,
   *     which fills the stage: `clientHeight` **900**, re-measured 2026-08-11.
   *     An earlier revision of this note and of the test constant said 898 —
   *     stated as measured, and wrong.)
   *   · the `◈ BEDROHUNGSARCHIV` row wears {@link MODAL_FRAME}, the mixer's own
   *     label-box material, at the row's FULL width — the owner's 2026-08-11
   *     refinement, quoted in that constant's doc-block. It costs **zero**
   *     height: `* { box-sizing: border-box }` (`index.html:29`) puts the
   *     hairline inside the row's `min-height`, and the row is floor-driven
   *     (its ink is ~24 design px in a ≥76.5 box), so the panel measures the
   *     same 829 design px with the frame as without it — re-measured in the
   *     same browser, both phone tiers. Only the archive row gets it: it is the
   *     one row that LEAVES the modal, and a frame around a slider row would
   *     fight the label box already inside it.
   */
  private static injectStyles(): void {
    if (typeof document === "undefined") return;
    if (document.getElementById(STYLE_ID)) return;
    const G = COLORS.SENTINEL_GREEN;
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = `
      #settings-card.settings-modal {
        width: ${withUiScale(440)};
        max-width: 86%;
        max-height: 94%;
        overflow-y: auto;
      }
      #settings-card .${MODAL_SHELL.title} {
        font-size: ${withUiScale(27)};
        letter-spacing: 0.22em;
      }
      #settings-card .settings-lbl { flex: 1; }
      #settings-card #settings-library {
        border: ${MODAL_FRAME.border};
        background: ${MODAL_FRAME.background};
      }
      #settings-card #settings-library:hover,
      #settings-card #settings-library:focus-visible {
        border-color: ${MODAL_FRAME.borderColorActive};
      }
      #settings-card .settings-chk {
        flex: none;
        width: ${withUiScale(20)};
        height: ${withUiScale(20)};
        border-radius: ${withUiScale(UI_SPACE.xxs)};
        border: 1px solid color-mix(in srgb, ${G} 55%, transparent);
        background: transparent;
      }
      #settings-card .settings-chk.settings-chk-on { background: ${G}; }
    `;
    document.head.appendChild(style);
  }
}
